import styled from '@emotion/styled'
import { BidderAdapter } from '@pubstack/common/src/adm/bidder-adapter'
import { ContextKey } from '@pubstack/common/src/contextKey'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { FunctionComponent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Colors } from '~/assets/style/colors'
import { AdmIntegrationLogo } from '~/components/AdmIntegrationLogo'
import { CollapsiblePanel } from '~/components/CollapsiblePanel'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { Skeleton } from '~/components/Skeleton'
import { Status } from '~/components/Status'
import { Toggle } from '~/components/Toggle'
import { PureAdmGamSiteConfigForm } from '~/modules/adm/inventory/catalog/adServers/PureAdmGamSiteConfigForm'
import { PureAdmPrebidSiteConfigForm } from '~/modules/adm/inventory/catalog/headerBidders/PureAdmPrebidSiteConfigForm'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Tooltip } from '~/components/Tooltip'
import { WithClassName } from '~/types/utils'

const WrapperPanel = styled(CollapsiblePanel)`
  margin-bottom: 20px;
  & ${AdmIntegrationLogo} {
    height: 30px;
  }
`
const Logo = styled.div`
  width: 500px;
`
const TamStatus = styled.div`
  display: flex;
  gap: 8px;
`
const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const WarningMessage = styled.div`
  display: inline-flex;
  padding: 10px;
  gap: 4px;
  background-color: ${Colors.Ghost};
  border: 1px solid ${Colors.Cobalt};
  border-radius: 4px;
`

type PureGamAndWrappersProps = WithClassName & {
  isLoading: boolean
  siteConfig: AdmSiteConfig
  baseCDNUrl: string
  hasNetworkId: boolean
  contextKeys: ContextKey[]
  bidderAdapters: BidderAdapter[]
  onCreateSiteConfig: (siteConfig: AdmSiteConfig, type: string) => Promise<void>
  onPrebidLinkClick: () => void
}

export const PureGamAndWrappers: FunctionComponent<PureGamAndWrappersProps> = ({
  isLoading,
  baseCDNUrl,
  siteConfig,
  hasNetworkId,
  contextKeys,
  bidderAdapters,
  onCreateSiteConfig,
  onPrebidLinkClick,
}) => {
  const navigate = useNavigate()
  // Switching TAM on for a media changes what it sends to Amazon on every auction — an owner's call.
  const { canEdit, disabledReason } = useScopePermissions()
  const gamState = (siteConfig?.adUnitPathConfig?.length ?? 0) > 0
  return !isLoading ? (
    <>
      <WrapperPanel
        title={
          <TitleWrapper>
            <Logo>
              <AdmIntegrationLogo baseCDNUrl={baseCDNUrl} name={'gam'} />
            </Logo>
            {gamState ? <Status state={'active'}>Configured</Status> : <Status state={'error'}>Not Configured</Status>}
          </TitleWrapper>
        }
        isCollapsible={{ trackingLabel: 'adm-sites-stacks-gam-configuration-collapse-apply' }}
      >
        {!hasNetworkId && (
          <WarningMessage>
            <Icon name={'warning'} />
            <span>
              To use GAM properly, you need to define the Network ID in{' '}
              <Link onClick={() => navigate('/monetization/one/integrations/gam')} label={'Integrations'} trackingLabel={'adm-sites-stacks-gam-integration-navigate'} />.
            </span>{' '}
          </WarningMessage>
        )}
        <PureAdmGamSiteConfigForm
          contextKeys={contextKeys}
          siteConfig={siteConfig}
          createSiteConfig={(data) => {
            onCreateSiteConfig(data, 'GAM')
          }}
        />
      </WrapperPanel>
      <WrapperPanel
        title={
          <TitleWrapper>
            <Logo>
              <AdmIntegrationLogo baseCDNUrl={baseCDNUrl} name={'prebid'} />
            </Logo>
            <Status state={'active'}>Active</Status> {/* Prebid is still 'active' */}
          </TitleWrapper>
        }
        isCollapsible={{ trackingLabel: 'adm-sites-stacks-prebid-configuration-collapse-apply' }}
      >
        <PureAdmPrebidSiteConfigForm siteConfig={siteConfig} onCreateSiteConfig={(data) => onCreateSiteConfig(data, 'Prebid')} bidderAdapters={bidderAdapters} onPrebidLinkClick={onPrebidLinkClick} />
      </WrapperPanel>
      <WrapperPanel
        title={
          <TitleWrapper>
            <Logo>
              <AdmIntegrationLogo baseCDNUrl={baseCDNUrl} name={'tam'} />
            </Logo>
            <TamStatus>
              {/* The reason sits on the label rather than the toggle: a disabled control dispatches no
                  hover of its own. */}
              <Toggle
                disabled={!canEdit}
                value={siteConfig.isTamEnabled}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onCreateSiteConfig({ ...siteConfig, isTamEnabled: !siteConfig.isTamEnabled }, 'TAM')
                }}
                trackingLabel={siteConfig.isTamEnabled ? 'adm-sites-stacks-tam-unset' : 'adm-sites-stacks-tam-set'}
              />
              <Tooltip title={disabledReason} disabled={canEdit}>
                <span>{siteConfig.isTamEnabled ? 'Enabled' : 'Disabled'}</span>
              </Tooltip>
            </TamStatus>
          </TitleWrapper>
        }
      />
    </>
  ) : (
    <>
      {[0, 1, 2].map((e) => (
        <WrapperPanel key={e} forceState={'closed'} hasBorderWhenOpened title={<Skeleton width={`15ch`} />} />
      ))}
    </>
  )
}
